// productsShared.js — helpers, constantes et hooks partagés du module Produits mobile.
// Aucune dépendance hors React + (optionnel) le script global `lucide`.
import React from "react";
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  ArrowRightLeft,
  Ban,
  Banknote,
  Boxes,
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  Clock,
  Copy,
  Eye,
  EyeOff,
  ExternalLink,
  FileSearch,
  FileText,
  Globe,
  Hammer,
  Home,
  Infinity as InfinityIcon,
  Layers,
  ListOrdered,
  Loader2,
  LogIn,
  LogOut,
  Menu,
  MessageSquare,
  MoreHorizontal,
  PackageCheck,
  PackageSearch,
  PaintRoller,
  Pencil,
  Percent,
  Pipette,
  Plus,
  PlusCircle,
  Power,
  Printer,
  RefreshCw,
  Reply,
  RotateCcw,
  Ruler,
  ScanLine,
  ScrollText,
  Search,
  ShieldCheck,
  ShoppingBag,
  SlidersHorizontal,
  Star,
  StarOff,
  Store,
  Tag,
  Trash2,
  Truck,
  Users,
  Warehouse,
  Wrench,
  X,
} from "lucide-react";

const ICONS = {
  activity: Activity,
  "alert-circle": AlertCircle,
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  "arrow-right-left": ArrowRightLeft,
  ban: Ban,
  banknote: Banknote,
  boxes: Boxes,
  camera: Camera,
  check: Check,
  "check-circle-2": CheckCircle2,
  "chevron-down": ChevronDown,
  "chevron-right": ChevronRight,
  clock: Clock,
  copy: Copy,
  eye: Eye,
  "eye-off": EyeOff,
  "external-link": ExternalLink,
  "file-search": FileSearch,
  "file-text": FileText,
  globe: Globe,
  hammer: Hammer,
  home: Home,
  infinity: InfinityIcon,
  layers: Layers,
  "list-ordered": ListOrdered,
  "loader-circle": Loader2,
  "log-in": LogIn,
  "log-out": LogOut,
  menu: Menu,
  "message-square": MessageSquare,
  "more-horizontal": MoreHorizontal,
  "package-check": PackageCheck,
  "package-search": PackageSearch,
  "paint-roller": PaintRoller,
  pencil: Pencil,
  percent: Percent,
  pipette: Pipette,
  plus: Plus,
  "plus-circle": PlusCircle,
  power: Power,
  printer: Printer,
  "refresh-cw": RefreshCw,
  reply: Reply,
  "rotate-ccw": RotateCcw,
  ruler: Ruler,
  "scan-line": ScanLine,
  "scroll-text": ScrollText,
  search: Search,
  "shield-check": ShieldCheck,
  "shopping-bag": ShoppingBag,
  "sliders-horizontal": SlidersHorizontal,
  star: Star,
  "star-off": StarOff,
  store: Store,
  tag: Tag,
  "trash-2": Trash2,
  truck: Truck,
  users: Users,
  warehouse: Warehouse,
  wrench: Wrench,
  x: X,
};

/* ---------------------------------------------------------------
   Icônes (seam d'intégration) — set Lucide (look "ligne").
   <Ico> rend un <i> STABLE que React conserve, et injecte le SVG
   À L'INTÉRIEUR (hors réconciliation React) : aucun conflit de DOM.
   → Charge le script lucide global dans ton app (voir README),
     OU remplace ce composant par lucide-react / tes <…Outlined/> Ant.
--------------------------------------------------------------- */
export function Ico({ name, className = "", style = undefined }) {
  const Icon = ICONS[name] || Circle;

  return (
    <i className={className} style={style} aria-hidden="true">
      <Icon />
    </i>
  );
}

/* Conservé pour compat — l'affichage des icônes est géré par <Ico>. */
export function useIcons() {}

/* ---------------------------------------------------------------
   Format & statut
--------------------------------------------------------------- */
// French decimal input is kept intact until the existing API payload is built.
export const parseProductNumber = (value) => Number(String(value ?? "").replace(/\s/g, "").replace(/,/g, "."));

export const fmt = (n) =>
  new Intl.NumberFormat("fr-FR").format(Math.round(Number(n) || 0));

export function productStatus(p) {
  const stock = Number(p.stock) || 0;
  const alert = Number(p.alert) || 0;
  if (p.stockMode === "unlimited") return { k: "ok", label: "Illimité", cls: "ok" };
  if (stock <= 0) return { k: "out", label: "Rupture", cls: "out" };
  if (stock <= alert) return { k: "low", label: "Stock bas", cls: "low" };
  return { k: "ok", label: "En stock", cls: "ok" };
}

const HUES = {
  Matériaux: 210, Couverture: 28, Finitions: 268,
  Plomberie: 190, Quincaillerie: 150, Outillage: 12,
};
export function thumbStyle(categoryName) {
  const h = HUES[categoryName] ?? 210;
  return { background: `linear-gradient(135deg,hsl(${h} 42% 52%),hsl(${h} 46% 38%))` };
}
export const initials = (name = "") =>
  name.replace(/[^A-Za-zÀ-ÿ0-9 ]/g, "").split(/\s+/).slice(0, 2)
    .map((w) => w[0] || "").join("").toUpperCase();

/* ---------------------------------------------------------------
   Référentiels d'options du formulaire.
   Les `value` correspondent AUX VALEURS BACKEND du vrai formulaire
   (product_type, stock_management_mode, barcode_symbology).
--------------------------------------------------------------- */
export const TYPE_OPTS = [
  { v: "Produits finis", label: "Produit fini", ico: "package-check" },
  { v: "Matières premières", label: "Matière première", ico: "boxes" },
];
export const STOCK_MODE_OPTS = [
  { v: "standard", label: "Standard", ico: "layers" },
  { v: "pre_order", label: "Précommande", ico: "clock" },
  { v: "unlimited", label: "Illimité", ico: "infinity" },
];
export const BARCODE_OPTS = [
  { v: "EAN13", label: "EAN-13" },
  { v: "CODE128", label: "CODE-128" },
  { v: "CODE39", label: "CODE-39" },
];

export const labelOf = (opts, v) => (opts.find((o) => o.v === v) || {}).label || v || "";
export const isRawMaterial = (type) => type === "Matières premières";

/* slug + code article : même logique que generateSlug / generateArticleCode */
export function genSlug(name = "") {
  return name.toString().toLowerCase().trim()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");
}
export function genCode(name = "") {
  const base = name.toString().toUpperCase().normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "").replace(/[^A-Z0-9 ]/g, "").trim().split(/\s+/);
  const prefix = (base[0] || "REF").slice(0, 3);
  const num = base.length > 1 ? base[1].slice(0, 2) : String(Math.floor(Math.random() * 90) + 10);
  return `${prefix}-${num}`;
}
